import { AppConstants } from "../../constants/AppConstants";
import { resetAllState } from "../../dataManagement/uiDb/resetAllState";
import { getStore } from "../../dataManagement/uiDb/storeAccess";
import HotspotHelper from "../helpers/HotspotHelper";
import { setHotspotBannerVisible, setUserLocation, setUserLocationName } from "../../dataManagement/uiDb/GeneralDb";
import { offerOpenSettings } from "../helpers/permissionRecovery";
import { clearFcmToken } from "../firebase/fcmService";
import VoiceCreateSessionStore from "../helpers/VoiceCreateSessionStore";
import { resetTurboSession } from "../helpers/TurboStore";



const genericUtility = {
  uiConfigurations: {},

  /**
   * Safely returns the keys of an object. Returns an empty array if the input is null, undefined, or not an object.
   * @param obj - The input object.
   * @returns An array of strings representing the object's keys.
   */
  safeObjectKeys(obj: any): string[] {
    if (!obj || typeof obj !== "object") {
      return [];
    }
    try {
      return Object.keys(obj);
    } catch {
      return [];
    }
  },

  /**
   * Converts a dictionary into a list of its values, ignoring the keys.
   * @param dictionary - The input dictionary.
   * @returns A list of all values in the dictionary.
   */
  dictionaryToList<T>(dictionary: Record<string, T>): T[] {
    return Object.values(dictionary);
  },

  setUiConfigurations(configs: Record<string, any>) {
    this.uiConfigurations = configs;
  },

  /**
   * A unique-enough client-side id for list keys / optimistic UI (e.g. a chat
   * message added to Redux before its request resolves).
   *
   * NOT a raw `crypto.randomUUID()` call: that API only exists in secure
   * contexts (HTTPS, or localhost/127.0.0.1). Testing over a LAN IP on plain
   * HTTP (e.g. `http://192.168.x.x:5173`, or any non-HTTPS deployment) is an
   * insecure context, so `crypto.randomUUID` is undefined there and throws
   * "crypto.randomUUID is not a function" — this happened in this app before.
   * Falls back to `crypto.getRandomValues` (works in insecure contexts too),
   * then to a timestamp+random string as a last resort.
   */
  generateId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
    return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  },
  getUiConfigurations(): Record<string, any> {
    return this.uiConfigurations;
  },

  /**
   * Converts a list of objects into a dictionary with the object's `id` as the key.
   * @param list - The input list of objects.
   * @returns A dictionary with keys as `object.id` and values as the objects.
   */
  listToDictionary<T extends { id: string; productId: string }>(
    list: T[]
  ): Record<string, T> {
    const dictionary: Record<string, T> = {};
    list.forEach((item) => {
      dictionary[item.productId] = item;
    });
    return dictionary;
  },

  parseJwt(token: string) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  },

  getCookie(name: string): string | null {
    if (this.isPWA()) {
      return localStorage.getItem(name);
    } else {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.trim().split("=");
        if (cookieName === name) {
          return cookieValue || null;
        }
      }
      return null;
    }
  },
  getCurrentUserId(): string | null {
    const raw = this.getCookie("userId");
    if (!raw) return null;
    // Older cookie writes appended "expires=..." into the value; strip if present.
    return raw.split("expires")[0] || null;
  },

  /**
   * Returns whether the user is currently logged in.
   * Checks for a stored token and verifies the JWT `exp` claim if present.
   */
  isUserLoggedIn(): boolean {
    const token = this.getCookie("token");
    if (!token) return false;

    try {
      const claims: any = this.parseJwt(token);
      if (!claims) return false;

      const now = Math.floor(Date.now() / 1000);
      if (claims.exp && typeof claims.exp === "number" && claims.exp <= now) {
        return false; // token expired
      }

      // Basic additional check: ensure userId exists
      const uid = this.getCookie("userId");
      if (!uid) return false;

      return true;
    } catch (e) {
      return false;
    }
  },

  getCurrentUserLocation(): Promise<{ latitude: number; longitude: number }> {
    return new Promise(async (resolve, reject) => {
      if (!navigator.geolocation) {
        return reject("Geolocation is not supported by your browser.");
      }

      // Check the current permission state before attempting
      let permissionState: PermissionState | null = null;
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        permissionState = status.state;
      } catch {
        // permissions API not supported — fall through and attempt anyway
      }

      // If permission was permanently denied, tell the user how to fix it
      if (permissionState === 'denied') {
        // In the Android app, offer to open system Settings directly.
        offerOpenSettings("Location is off for NineFinds.");
        return reject(
          "📍 Location permission is blocked. Please click the lock icon (🔒) in your address bar → Site settings → Location → Allow, then try again."
        );
      }

      // Permission is 'granted' or 'prompt' — request location (browser will show prompt if needed)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            // In the Android app, offer to open system Settings directly.
            offerOpenSettings("Location is off for NineFinds.");
            return reject(
              "📍 Location access is required. NineFinds is a neighbourhood marketplace — please allow location access to list here."
            );
          }
          if (error.code === error.POSITION_UNAVAILABLE) {
            return reject("Location information is currently unavailable. Please try again.");
          }
          if (error.code === error.TIMEOUT) {
            return reject("Location request timed out. Please check your GPS/network and try again.");
          }
          return reject("An unknown location error occurred. Please try again.");
        },
        {
          maximumAge: 0,
          timeout: AppConstants.TIMEOUTS.DEFAULT,
          enableHighAccuracy: true,
        }
      );
    });
  },

  setTokenDetails(accessToken: string, userId: string, refreshToken: string) {
    if (accessToken) {
      const expires = new Date(
        Date.now() + 7 * 24 * 60 * 60 * 1000
      ).toUTCString(); // 7 days from now

      // Clear existing cookies first to prevent duplicates
      this.deleteCookie("token");
      this.deleteCookie("userId");

      // Set new cookies
      document.cookie = `token=${accessToken};path=/;expires=${expires};`;
      document.cookie = `userId=${userId};path=/;expires=${expires};`;
      document.cookie = `refreshToken=${refreshToken};path=/;expires=${expires};`;

      if (this.isPWA()) {
        localStorage.setItem("token", accessToken);
        localStorage.setItem("userId", userId);
        localStorage.setItem("refreshToken", refreshToken);
      }
    }
  },

  deleteCookie(name: string) {
    // Delete cookie with multiple methods to ensure it's removed
    document.cookie = `${name}=; Max-Age=0; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`;
  },
  deleteLocalStorage(name: string) {
    localStorage.removeItem(name);
  },
  isPWA() {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches
    );
  },

  getReturnUrl(): string {
    return sessionStorage.getItem("returnUrl") || "/home";
  },

  setReturnUrl(url: string): void {
    sessionStorage.setItem("returnUrl", url);
  },

  clearReturnUrl(): void {
    sessionStorage.removeItem("returnUrl");
  },

  // Persist simple origin info across redirects (e.g., login)
  setOriginFrom(path: string): void {
    sessionStorage.setItem("originFrom", path);
  },
  getOriginFrom(): string | null {
    return sessionStorage.getItem("originFrom");
  },
  clearOriginFrom(): void {
    sessionStorage.removeItem("originFrom");
  },

  // Whether the user has dismissed the "use my location" prompt this tab session
  getLocationPromptDismissed(): boolean {
    try {
      return sessionStorage.getItem("locationPromptDismissed") === "true";
    } catch {
      return false;
    }
  },
  setLocationPromptDismissed(): void {
    try {
      sessionStorage.setItem("locationPromptDismissed", "true");
    } catch {
      // sessionStorage unavailable (e.g. sandboxed iframe) — nothing to persist
    }
  },
  clearLocationPromptDismissed(): void {
    try {
      sessionStorage.removeItem("locationPromptDismissed");
    } catch {
      // sessionStorage unavailable — nothing to clear
    }
  },

  logout(navigate: (path: string, options?: { replace: boolean }) => void) {
    // Deregister this device's FCM token on the backend BEFORE clearing the auth
    // cookie. clearFcmToken -> postRequest reads the Bearer token and dispatches
    // the request synchronously (before its first await), so the still-present
    // cookie is captured even though we delete it immediately below. Fire-and-
    // forget: it is best-effort and must never block logout.
    clearFcmToken();

    // Turbo lives outside Redux: forget its availability answers and switch positions for the next user.
    resetTurboSession();

    // Clear Redux state - this will reset all slices to their initial state
    getStore().dispatch(resetAllState());
    // Unfinished create-by-voice photos stay on this device otherwise.
    void VoiceCreateSessionStore.clearAll();

    // Clear authentication data
    this.deleteCookie("token");
    this.deleteCookie("userId");
    this.deleteCookie("refreshToken");
    this.deleteLocalStorage("token");
    this.deleteLocalStorage("userId");
    this.deleteLocalStorage("refreshToken");

    // Clear any return URL that might be stored
    this.clearReturnUrl();
    this.clearLocationPromptDismissed();

    // Navigate to login page using React Router
    navigate("/home", { replace: true });
  },

  // ── Location persistence (localStorage — survives tab close & works across tabs) ──

  getStoredLocation(): { userLatitude: number; userLongitude: number; userLocationDisplayName?: string } | null {
    try {
      const raw = localStorage.getItem("nine_user_location");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (
        typeof parsed.userLatitude !== "number" ||
        typeof parsed.userLongitude !== "number"
      )
        return null;
      return parsed;
    } catch {
      return null;
    }
  },

  setStoredLocation(loc: { userLatitude?: number; userLongitude?: number; userLocationDisplayName?: string }): void {
    try {
      localStorage.setItem("nine_user_location", JSON.stringify(loc));
    } catch {
      // storage full or unavailable — non-fatal
    }
  },

  clearStoredLocation(): void {
    localStorage.removeItem("nine_user_location");
  },

  // ── Location Orchestration Logic ──

  /**
   * Checks if the given coordinates are within a hotspot and shows the banner if not.
   */
  checkHotspotAndBanner(
    coords: { latitude?: number; longitude?: number; userLatitude?: number; userLongitude?: number },
    dispatch: any
  ) {
    const lat = coords.userLatitude ?? coords.latitude;
    const lng = coords.userLongitude ?? coords.longitude;
    if (lat == null || lng == null) return;
    const isInside = HotspotHelper.isInsideHotspot(lat, lng);
    if (!isInside) {
      dispatch(setHotspotBannerVisible(true));
    }
  },

  /**
   * Initial resolution of user location on app startup.
   * Prioritizes Profile -> Redux -> Storage.
   */
  resolveStartupLocation(
    dispatch: any,
    openMandatoryModal: (open: boolean) => void,
    userPreferences: any
  ) {
    const reduxLocation = getStore().getState().general.UserLocation;

    // Use the comprehensive resolver
    const bestLocation = this.resolveUserLocation(userPreferences, reduxLocation);

    if (bestLocation) {
      // If we found a location (from profile, redux, or storage), sync it to all layers
      dispatch(setUserLocation(bestLocation));
      if (bestLocation.userLocationDisplayName) {
        dispatch(setUserLocationName(bestLocation.userLocationDisplayName));
      }
      this.setStoredLocation(bestLocation);
      this.checkHotspotAndBanner(bestLocation, dispatch);
      return;
    }

    // No location found anywhere -> Mandatory Modal
    openMandatoryModal(true);
  },

  /**
   * Finalizes location selection when user chooses from the mandatory modal.
   */
  handleLocationSelected(dispatch: any, closeMandatoryModal: (open: boolean) => void) {
    closeMandatoryModal(false);
    const loc = getStore().getState().general.UserLocation;
    if (loc) {
      const lat = loc.userLatitude;
      const lng = loc.userLongitude;
      if (lat != null && lng != null) {
        this.setStoredLocation(loc);
        this.checkHotspotAndBanner(loc, dispatch);
      }
    }
  },

  // ── Location Accessor Utilities ──

  /**
   * Resolves the best available UserLocation using a fallback chain:
   *   1. userPreferences.userLocation
   *   2. generalDb.UserLocation (Redux, set during current session)
   *   3. localStorage stored location
   * Returns null only if no location exists anywhere.
   */
  resolveUserLocation(
    userPreferences: any,
    generalDbLocation?: any
  ): { userLatitude?: number; userLongitude?: number; userLocationDisplayName?: string } | null {

    const isValidLocation = (loc: any): boolean =>
      loc?.userLatitude != null &&
      loc?.userLongitude != null &&
      loc?.userLocationDisplayName != null &&
      loc?.userLocationDisplayName !== "";

    // 1. User preferences (backend-synced, most authoritative)
    const prefLoc = userPreferences?.userLocation;
    if (isValidLocation(prefLoc)) return prefLoc;

    // 2. GeneralDb Redux state (set during current session)
    if (isValidLocation(generalDbLocation)) return generalDbLocation;

    // 3. localStorage (persisted across sessions/tabs)
    const stored = this.getStoredLocation();
    if (isValidLocation(stored)) return stored;

    // 4. Hardcoded fallback — always returns a usable location so the app
    //    never blocks the user with a mandatory location modal.
    return AppConstants.DEFAULT_LOCATION;
  },

  /**
   * Truncates text for responsive display.
   * Shorter on mobile, longer on desktop.
   */
  truncateForDisplay(
    text: string,
    mobileMaxLength: number = 45,
    desktopMaxLength: number = 55
  ): string {
    const isMobile = window.innerWidth < 640;
    const maxLength = isMobile ? mobileMaxLength : desktopMaxLength;
    if (text.length <= maxLength) return text;
    const cutoff = isMobile ? mobileMaxLength - 3 : desktopMaxLength - 3;
    return text.substring(0, cutoff) + "...";
  },

  /**
   * Extracts the display name from a UserLocation object.
   * Returns a fallback string if no name is available.
   * @param truncate — when true, shortens long names for responsive display.
   */
  getDisplayLocationName(
    location: { userLocationDisplayName?: string } | null | undefined,
    truncate: boolean = true,
    fallback: string = "Select location"
  ): string {
    const name = location?.userLocationDisplayName;
    if (!name || name === "") return fallback;
    if (!truncate) return name;
    return this.truncateForDisplay(name);
  },

  /**
   * Extracts the latitude from a UserLocation object.
   */
  getLocationLatitude(
    location: { userLatitude?: number } | null | undefined
  ): number | null {
    return location?.userLatitude ?? null;
  },

  /**
   * Extracts the longitude from a UserLocation object.
   */
  getLocationLongitude(
    location: { userLongitude?: number } | null | undefined
  ): number | null {
    return location?.userLongitude ?? null;
  },

  /**
   * Pure role resolver — determines any user's role in a business given an
   * explicit userId. No store access; safe to call from any context.
   *
   *   "owner"        — effective owner (ownerId == userId, or ownerId is null AND creatorId == userId)
   *   "collaborator" — listed in collaborators
   *   "creator"      — created it but someone else owns it now
   *   "viewer"       — anyone else / userId not provided
   */
  resolveRoleInBusiness(
    userId: string | null | undefined,
    creatorId: string | null | undefined,
    ownerId: string | null | undefined,
    collaborators: { collaboratorId?: string }[] | null | undefined
  ): "owner" | "collaborator" | "creator" | "viewer" {
    if (!userId) return "viewer";
    const effectiveOwnerId = ownerId ?? creatorId ?? null;
    if (effectiveOwnerId === userId) return "owner";
    if (collaborators?.some((c) => c.collaboratorId === userId)) return "collaborator";
    if (creatorId === userId) return "creator";
    return "viewer";
  },

  /**
   * Resolves the current user's role in a given business.
   * Thin wrapper around resolveRoleInBusiness using the logged-in user's ID.
   */
  getMyRoleInThisBusiness(
    creatorId: string | null | undefined,
    ownerId: string | null | undefined,
    collaborators: { collaboratorId?: string }[] | null | undefined
  ): "owner" | "collaborator" | "creator" | "viewer" {
    return this.resolveRoleInBusiness(this.getCurrentUserId(), creatorId, ownerId, collaborators);
  },

  /**
   * Extracts the storage object path from a Supabase URL (signed or public).
   * Examples:
   *   https://<host>/storage/v1/object/sign/<bucket>/<path>?token=...
   *   https://<host>/storage/v1/object/public/<bucket>/<path>
   * Returns "<path>" (everything after the bucket segment) or null if parsing fails.
   *
   * TODO: drop this once the backend accepts the URL directly on DELETE —
   * peeking at Supabase URL structure is brittle.
   */
  objectPathFromSignedUrl(url: string): string | null {
    if (!url) return null;
    try {
      const u = new URL(url);
      const markers = ["/object/sign/", "/object/public/", "/object/authenticated/"];
      for (const marker of markers) {
        const idx = u.pathname.indexOf(marker);
        if (idx === -1) continue;
        const afterBucket = u.pathname.slice(idx + marker.length); // <bucket>/<path>
        const parts = afterBucket.split("/");
        if (parts.length < 2) return null;
        return parts.slice(1).join("/"); // drop bucket → "<path>"
      }
      return null;
    } catch {
      return null;
    }
  },

  async encryptPassword(password: string) {
    if (!password) return "";
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await window.crypto?.subtle?.digest("SHA-256", data);
    if (!hashBuffer) return "";
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  },
};

export default genericUtility;
 